import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Eye, EyeOff, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import {
  useGetApiUsersCurrentNotifications,
  useGetApiUsersCurrentNotificationsCount,
  usePatchApiUsersCurrentNotificationsNotificationIdStatus,
  getGetApiUsersCurrentNotificationsQueryKey,
  getGetApiUsersCurrentNotificationsCountQueryKey,
} from '@/api/generated/user/user'
import { NotificationStatus } from '@/api/model/notificationStatus'
import { PortalNotificationStatus } from '@/api/model/portalNotificationStatus'
import { PortalPayloadType } from '@/api/model/portalPayloadType'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 10

function toUtcDate(ts: string | null | undefined): Date | null {
  if (!ts) return null
  return new Date(ts.endsWith('Z') ? ts : ts + 'Z')
}

function formatDateTime(ts: string | null | undefined) {
  const date = toUtcDate(ts)
  if (!date) return ''
  return date.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function formatDateOnly(ts: string | null | undefined) {
  const date = toUtcDate(ts)
  if (!date) return ''
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function parsePayload(payload: string | null | undefined): Record<string, any> | null {
  try {
    return payload ? JSON.parse(payload) : null
  } catch {
    return null
  }
}

function lc(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase() : ''
}

type NotificationLink = { to: string; labelKey: string }
type NotificationDescription = { text: string; link: NotificationLink | null }

// A notification only carries a `payloadType` + a JSON payload; the display text
// and deep-link are derived here from the payload, not shown raw. Text and link
// are produced together because one payloadType can mean two different things
// (see ActionModuleNoFulfillmentStateReminder below).
//
// Deep-links point at the coarse pages that exist in this portal today
// (/service-modules, /incentivation, /action-modules) — the type-specific target
// pages (absence, timetracking, incentivation subtypes) aren't built yet.
function describeNotification(
  item: any,
  t: (key: string, options?: Record<string, unknown>) => string,
): NotificationDescription {
  const payload = parsePayload(item.payload)
  if (!payload) return { text: item.payloadType ?? '', link: null }

  switch (item.payloadType) {
    case PortalPayloadType.ActionModuleNoScoreReminder:
      return {
        text: t('notifications.types.ActionModuleNoScoreReminder', { subtype: payload.subtype ?? '-' }),
        link: { to: '/incentivation', labelKey: 'notifications.links.openIncentivation' },
      }

    case PortalPayloadType.ActionModuleNoFulfillmentStateReminder: {
      // Same payloadType, two meanings: an absence-approval reminder
      // (serviceModuleType === 'absence', carries start/end/reason/status) vs.
      // an incentivation "no adjustments yet" reminder (carries `subtype`).
      if (lc(payload.serviceModuleType) === 'absence') {
        const reasonKey = lc(payload.absenceReason)
        const statusKey = lc(payload.newApproval)
        return {
          text: t('notifications.types.ActionModuleAbsenceReminder', {
            moduleName: payload.serviceModuleName ?? '-',
            start: formatDateOnly(payload.startTimestamp) || '-',
            end: formatDateOnly(payload.endTimestamp) || '-',
            reason: reasonKey
              ? t(`notifications.absence.reason.${reasonKey}`, { defaultValue: payload.absenceReason ?? '-' })
              : '-',
            status: statusKey
              ? t(`notifications.absence.status.${statusKey}`, { defaultValue: payload.newApproval ?? '-' })
              : '-',
          }),
          link: { to: '/service-modules', labelKey: 'notifications.links.openAbsences' },
        }
      }
      return {
        text: t('notifications.types.ActionModuleNoFulfillmentStateReminder', { subtype: payload.subtype ?? '-' }),
        link: { to: '/incentivation', labelKey: 'notifications.links.openIncentivation' },
      }
    }

    case PortalPayloadType.ActionModuleTimeSheetApprovalNeeded:
      return {
        text: t('notifications.types.ActionModuleTimeSheetApprovalNeeded', {
          moduleName: payload.serviceModuleName ?? '-',
          start: formatDateTime(payload.startTimestamp) || '-',
          end: formatDateTime(payload.endTimestamp) || '-',
        }),
        link: { to: '/service-modules', labelKey: 'notifications.links.openTimesheets' },
      }

    case PortalPayloadType.ActionModuleTimeQuotaRequired:
      return {
        text: t('notifications.types.ActionModuleTimeQuotaRequired', {
          employeeName: [payload.employeeFirstName, payload.employeeLastName].filter(Boolean).join(' ') || '-',
          moduleName: payload.serviceModuleName ?? '-',
          date: formatDateOnly(payload.timestamp) || '-',
        }),
        link: { to: '/service-modules', labelKey: 'notifications.links.openServiceModules' },
      }

    case PortalPayloadType.ActionModuleRewardDeclined:
      return {
        text: t('notifications.types.ActionModuleRewardDeclined', {
          userName: payload.decliningUserName ?? '-',
          moduleName: payload.actionModuleName ?? '-',
          start: formatDateOnly(payload.startDate) || '-',
          end: formatDateOnly(payload.endDate) || '-',
        }),
        link: { to: '/action-modules', labelKey: 'notifications.links.openActionModules' },
      }
    case PortalPayloadType.AutoTranslationCompleted:
      return {
        text: t('notifications.types.AutoTranslationCompleted', {
          entityType: payload.entityType ?? '-',
          translated: payload.translated ?? 0,
          skipped: payload.skipped ?? 0,
          failed: payload.failed ?? 0,
        }),
        link: null,
      }

    case PortalPayloadType.PersonalDocumentsImportCompleted: {
      const textKey = 'notifications.types.PersonalDocumentsImportCompleted'
      return {
        text: t(textKey, {
          successCount: payload.successCount ?? 0,
          failedCount: payload.failedCount ?? 0,
        }),
        link: payload.serviceModuleId
          ? {
              to: `/service-modules?open=${payload.serviceModuleId}`,
              labelKey: 'notifications.links.openPersonalDocuments',
            }
          : null,
      }
    }
    case PortalPayloadType.EmployeesImportCompleted:
      return {
        text: t('notifications.types.EmployeesImportCompleted', {
          addedCount: payload.addedCount ?? 0,
          removedCount: payload.removedCount ?? 0,
        }),
        link: { to: '/employees', labelKey: 'notifications.links.openEmployees' },
      }

    default:
      return { text: item.payloadType ?? '', link: null }
  }
}

export function NotificationPopoverContent() {
  const { t } = useTranslation('header')
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'unread' | 'all'>('unread')
  const [page, setPage] = useState(1)

  const status = tab === 'unread' ? NotificationStatus.Unread : NotificationStatus.All
  const offset = (page - 1) * PAGE_SIZE

  const { data: listData } = useGetApiUsersCurrentNotifications({ offset, limit: PAGE_SIZE, status })
  // Always fetch total "All" count for the tab label
  const { data: allListData } = useGetApiUsersCurrentNotifications({ offset: 0, limit: 1, status: NotificationStatus.All })
  const { data: countData } = useGetApiUsersCurrentNotificationsCount()
  const patch = usePatchApiUsersCurrentNotificationsNotificationIdStatus()

  const raw = (listData as any)?.data ?? listData
  const items: any[] = raw?.items ?? []
  const totalCount: number = raw?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const unreadCount: number = (countData as any)?.data ?? countData ?? 0
  const allRaw = (allListData as any)?.data ?? allListData
  const allCount: number = allRaw?.totalCount ?? 0

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getGetApiUsersCurrentNotificationsQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetApiUsersCurrentNotificationsCountQueryKey() })
  }

  async function toggleStatus(id: number, currentStatus: string) {
    const next = currentStatus === 'Read' ? PortalNotificationStatus.Unread : PortalNotificationStatus.Read
    await patch.mutateAsync({ notificationId: id, data: next })
    invalidate()
  }

  return (
    <div className="flex flex-col">
      {/* Tab header */}
      <div className="flex border-b">
        <button
          onClick={() => { setTab('unread'); setPage(1) }}
          className={cn('flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2', tab === 'unread' ? 'text-foreground border-primary' : 'text-muted-foreground border-transparent hover:text-foreground')}
        >
          {t('notifications.tabs.unread', { count: unreadCount })}
        </button>
        <button
          onClick={() => { setTab('all'); setPage(1) }}
          className={cn('flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2', tab === 'all' ? 'text-foreground border-primary' : 'text-muted-foreground border-transparent hover:text-foreground')}
        >
          {t('notifications.tabs.all', { count: allCount })}
        </button>
      </div>

      {/* Notification list */}
      <ScrollArea className="h-72">
        <div className="p-2 space-y-1">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6 italic">{t('notifications.empty')}</p>
          ) : items.map((item: any) => {
            const isRead = item.status === 'Read'
            const { text, link } = describeNotification(item, t)
            const date = formatDateTime(item.createdTimestamp)
            return (
              <div
                key={item.id}
                className={cn(
                  'flex items-start gap-2 p-2.5 rounded-md',
                  isRead ? 'bg-muted/30' : 'bg-primary/5 border-l-2 border-primary'
                )}
              >
                <div className="flex-1 min-w-0 space-y-1">
                  {text && <p className="text-xs leading-snug">{text}</p>}
                  {link && (
                    <Link to={link.to} className="text-xs text-primary hover:underline">
                      {t(link.labelKey)}
                    </Link>
                  )}
                  <p className="text-[10px] text-muted-foreground">{date || t('notifications.noDate')}</p>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={() => toggleStatus(item.id, item.status)}
                    >
                      {isRead ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isRead ? t('notifications.markUnread') : t('notifications.markRead')}</TooltipContent>
                </Tooltip>
              </div>
            )
          })}
        </div>
      </ScrollArea>

      <div className="flex items-center justify-center gap-1 border-t px-2 py-2 bg-muted/20">
        <Button variant="outline" size="icon" className="size-7" disabled={page <= 1} onClick={() => setPage(1)}>
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="size-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-7 h-7 flex items-center justify-center rounded-md border text-xs font-semibold px-2">
          {page}
        </span>
        <Button variant="outline" size="icon" className="size-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="size-7" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
